import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ConditionBuilder } from '../condition-builder/ConditionBuilder';
import { useKeyboardSave } from './use-keyboard-save';
import type { SetRuleElement, Condition } from '@/types/policy-editor';

interface SetRuleFormProps {
    element: SetRuleElement | null;
    onSave: (element: SetRuleElement) => void;
    onCancel: () => void;
}

export function SetRuleForm({ element, onSave, onCancel }: SetRuleFormProps) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [memberExpression, setMemberExpression] = useState('');
    const [conditions, setConditions] = useState<Condition[]>([]);

    useEffect(() => {
        if (element) {
            setName(element.name);
            setDescription(element.description ?? '');
            setMemberExpression(element.memberExpression);
            setConditions(element.conditions);
        } else {
            setName(''); setDescription(''); setMemberExpression(''); setConditions([]);
        }
    }, [element]);

    const handleSave = useCallback(() => {
        if (!name.trim() || !memberExpression.trim()) return;
        onSave({
            id: element?.id ?? `el-${Date.now()}`,
            elementType: 'setRule',
            name,
            description: description || undefined,
            memberExpression,
            conditions,
        });
    }, [element, name, description, memberExpression, conditions, onSave]);

    useKeyboardSave(handleSave, !!name.trim() && !!memberExpression.trim());

    return (
        <div className="flex h-full flex-col">
            <div className="flex-1 space-y-4 overflow-y-auto p-4">
                <div className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-700">
                    A set rule collects values into a set. Each time the conditions are true,
                    the member expression is added to the set.
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="set-name">Set name</Label>
                        <Input id="set-name" placeholder="e.g. allowed_profiles, valid_models" value={name} onChange={(e) => setName(e.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="set-desc">Description</Label>
                        <Input id="set-desc" placeholder="What values this set collects" value={description} onChange={(e) => setDescription(e.target.value)} />
                    </div>
                </div>

                <div className="space-y-1.5">
                    <Label htmlFor="set-member">Member expression</Label>
                    <Input id="set-member" className="font-mono text-xs" placeholder="e.g. net.id, profile_name" value={memberExpression} onChange={(e) => setMemberExpression(e.target.value)} />
                    <p className="text-[11px] text-slate-400">The value added to the set when conditions are met</p>
                </div>

                <ConditionBuilder conditions={conditions} onChange={setConditions} />
            </div>

            <div className="flex items-center justify-between border-t px-4 py-3">
                <span className="text-[10px] text-slate-400">Ctrl+Enter to save</span>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
                    <Button size="sm" onClick={handleSave} disabled={!name.trim() || !memberExpression.trim()}>Save</Button>
                </div>
            </div>
        </div>
    );
}
